import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import type { UserProfileDraft } from '@/features/admin-users/api';

const EMPTY_DRAFT: UserProfileDraft = { firstName: '', lastName: '', title: '' };

/**
 * The user page's "Overview" section (PTV2-1451): editable First/Last name (+ Title). Plane-agnostic —
 * the host supplies the demographics + the save handler. (Linked identities are their own section.)
 */
export function UserOverviewSection({
  demographics,
  loading,
  canEdit,
  saving,
  saveError,
  onSave,
}: {
  readonly demographics: UserProfileDraft | undefined;
  readonly loading: boolean;
  readonly canEdit: boolean;
  readonly saving: boolean;
  readonly saveError: unknown;
  readonly onSave: (draft: UserProfileDraft) => void;
}): ReactElement {
  const { t } = useTranslation();
  const fp = 'userRecord.overview';

  const initial = demographics ?? EMPTY_DRAFT;
  const [draft, setDraft] = useState<UserProfileDraft>(initial);

  // Reseed the form when the loaded demographics change (initial load / cache refresh / different user).
  useEffect(() => {
    if (demographics !== undefined) {
      setDraft(demographics);
    }
  }, [demographics]);

  function set(field: keyof UserProfileDraft, value: string): void {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  const dirty =
    draft.firstName !== initial.firstName ||
    draft.lastName !== initial.lastName ||
    draft.title !== initial.title;
  const valid = draft.firstName.trim().length > 0 && draft.lastName.trim().length > 0;

  return (
    <section aria-labelledby="user-overview-heading" className="space-y-4 max-w-xl">
      <div>
        <h2 id="user-overview-heading" className="text-base font-semibold text-foreground">
          {t(`${fp}.heading`)}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{t(`${fp}.subheading`)}</p>
      </div>

      {loading ? (
        <div className="space-y-2" aria-hidden="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full rounded-md" />
          ))}
        </div>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canEdit && valid && dirty) onSave(draft);
          }}
        >
          <Field id="user-first-name" label={t(`${fp}.fields.firstName`)} required>
            <Input
              id="user-first-name"
              value={draft.firstName}
              disabled={!canEdit || saving}
              onChange={(e) => {
                set('firstName', e.target.value);
              }}
            />
          </Field>
          <Field id="user-last-name" label={t(`${fp}.fields.lastName`)} required>
            <Input
              id="user-last-name"
              value={draft.lastName}
              disabled={!canEdit || saving}
              onChange={(e) => {
                set('lastName', e.target.value);
              }}
            />
          </Field>
          <Field id="user-title" label={t(`${fp}.fields.title`)}>
            <Input
              id="user-title"
              value={draft.title}
              disabled={!canEdit || saving}
              onChange={(e) => {
                set('title', e.target.value);
              }}
            />
          </Field>

          {saveError !== undefined && saveError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t(`${fp}.saveError`, { code: getErrorCodeForUi(saveError) })}
            </p>
          )}

          {canEdit && (
            <div className="flex justify-end">
              <Button type="submit" loading={saving} disabled={!valid || !dirty}>
                {t(`${fp}.save`)}
              </Button>
            </div>
          )}
        </form>
      )}
    </section>
  );
}

function Field({
  id,
  label,
  required,
  children,
}: {
  readonly id: string;
  readonly label: string;
  readonly required?: boolean;
  readonly children: ReactElement;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
        {required === true && <span className="text-red-600"> *</span>}
        {required !== true && (
          <span className="ml-1 text-xs font-normal text-muted-foreground">
            {t('userRecord.overview.optional')}
          </span>
        )}
      </label>
      {children}
    </div>
  );
}
